class TextImageCollectionTmb extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('.tic');
    this.scrollEl = this.querySelector('[data-tic-products-scroll]');
    this.btnUp = this.querySelector('[data-tic-scroll-up]');
    this.btnDown = this.querySelector('[data-tic-scroll-down]');
    this.imageWrap = this.root?.querySelector('[data-tic-image-wrap]');
    this.productsPanel = this.root?.querySelector('[data-tic-products-panel]');
    this.scrollWrap = this.root?.querySelector('.tic__products-scroll-wrap');
    this.textInner = this.root?.querySelector('[data-tic-text-inner]');

    if (!this.scrollEl || !this.root) return;

    this.showScrollNav = this.dataset.showScrollNav === 'true';
    this.mobileMq = window.matchMedia('(max-width: 989px)');
    this._retryAttempts = 0;
    this._layoutKey = '';

    this._onScroll = () => this.updateNavState();
    this.scrollEl.addEventListener('scroll', this._onScroll, { passive: true });

    if (this.showScrollNav) {
      this.btnUp?.addEventListener('click', (event) => this.onNavClick(event, -1));
      this.btnDown?.addEventListener('click', (event) => this.onNavClick(event, 1));

      this._onTouchMove = (event) => this.blockManualScroll(event);
      this._onWheel = (event) => this.blockManualScroll(event);
      this._onKeyDown = (event) => this.blockKeyboardScroll(event);

      this.scrollEl.addEventListener('touchmove', this._onTouchMove, { passive: false });
      this.scrollEl.addEventListener('wheel', this._onWheel, { passive: false });
      this.scrollEl.addEventListener('keydown', this._onKeyDown);
    } else {
      this.scrollEl.style.overflowY = 'hidden';
    }

    this._onResize = () => this.forceRefreshLayout();
    window.addEventListener('resize', this._onResize, { passive: true });

    this._onWindowLoad = () => this.forceRefreshLayout();
    window.addEventListener('load', this._onWindowLoad, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.refreshLayout());
      if (this.imageWrap) this._ro.observe(this.imageWrap);
      if (this.productsPanel) this._ro.observe(this.productsPanel);
      if (this.textInner) this._ro.observe(this.textInner);
      const list = this.scrollEl.querySelector('.tic__products-list');
      if (list) this._ro.observe(list);
    }

    if (this.imageWrap && typeof IntersectionObserver !== 'undefined') {
      this._io = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.forceRefreshLayout();
          }
        },
        { root: null, threshold: 0.01 }
      );
      this._io.observe(this.imageWrap);
    }

    const image = this.imageWrap?.querySelector('img');
    if (image) {
      if (!image.complete) {
        image.addEventListener('load', () => this.forceRefreshLayout(), { once: true });
      }
      image.addEventListener('error', () => this.forceRefreshLayout(), { once: true });
    }

    this.forceRefreshLayout();
    requestAnimationFrame(() => this.forceRefreshLayout());
    requestAnimationFrame(() => requestAnimationFrame(() => this.forceRefreshLayout()));

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.forceRefreshLayout());
    }

    this._settleTimers = [150, 400, 900, 1600].map((delay) =>
      window.setTimeout(() => this.forceRefreshLayout(), delay)
    );
  }

  disconnectedCallback() {
    this.scrollEl?.removeEventListener('scroll', this._onScroll);
    this.scrollEl?.removeEventListener('touchmove', this._onTouchMove);
    this.scrollEl?.removeEventListener('wheel', this._onWheel);
    this.scrollEl?.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('load', this._onWindowLoad);
    this._ro?.disconnect();
    this._io?.disconnect();
    clearTimeout(this._retryTimer);
    if (this._settleTimers) {
      this._settleTimers.forEach((timer) => window.clearTimeout(timer));
    }
  }

  isMobileViewport() {
    return this.mobileMq?.matches ?? window.innerWidth <= 989;
  }

  blockManualScroll(event) {
    if (!this.showScrollNav) return;
    event.preventDefault();
  }

  blockKeyboardScroll(event) {
    if (!this.showScrollNav) return;

    const keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
    if (keys.includes(event.key)) {
      event.preventDefault();
    }
  }

  forceRefreshLayout() {
    this._layoutKey = '';
    this.refreshLayout();
  }

  refreshLayout() {
    if (this._layoutFrame) return;

    this._layoutFrame = requestAnimationFrame(() => {
      this._layoutFrame = null;
      this.resolveSectionHeight();
      const synced = this.syncDynamicLayout();
      this.positionScrollNav();
      this.updateNavState();

      if (!synced) {
        this.scheduleLayoutRetry();
      } else {
        this._retryAttempts = 0;
      }
    });
  }

  scheduleLayoutRetry() {
    if (this._retryAttempts >= 20) return;

    this._retryAttempts += 1;
    const delay = Math.min(50 * this._retryAttempts, 800);

    clearTimeout(this._retryTimer);
    this._retryTimer = setTimeout(() => this.forceRefreshLayout(), delay);
  }

  getListGap(list) {
    const listStyle = window.getComputedStyle(list);
    return (
      parseFloat(listStyle.rowGap) ||
      parseFloat(listStyle.gap) ||
      parseFloat(getComputedStyle(this.root).getPropertyValue('--tic-card-list-gap')) ||
      10
    );
  }

  getBlockHeight(element) {
    if (!element) return 0;

    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      rect.height +
      (parseFloat(style.marginTop) || 0) +
      (parseFloat(style.marginBottom) || 0)
    );
  }

  getProductsOverhead() {
    let overhead = 0;

    const heading = this.productsPanel?.querySelector('.tic__collection-heading');
    const footerLink = this.productsPanel?.querySelector('.tic__collection-link');

    overhead += this.getBlockHeight(heading);
    overhead += this.getBlockHeight(footerLink);

    return Math.ceil(overhead);
  }

  getCardMinHeight() {
    const min = this.isMobileViewport()
      ? parseInt(this.dataset.cardMinMobile || '120', 10)
      : parseInt(this.dataset.cardMinDesktop || '100', 10);

    return Number.isFinite(min) ? min : 100;
  }

  getBaseSectionHeight() {
    const fromData = parseInt(this.dataset.sectionHeight || '', 10);
    if (Number.isFinite(fromData) && fromData > 0) return fromData;

    const fromVar = parseFloat(getComputedStyle(this.root).getPropertyValue('--tic-section-height'));
    return Number.isFinite(fromVar) && fromVar > 0 ? fromVar : 620;
  }

  getVerticalPadding() {
    const style = getComputedStyle(this.root);
    return (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
  }

  getTextExpansionMargin() {
    return 12;
  }

  getTextContentHeight() {
    if (!this.textInner) return 0;

    return Math.ceil(Math.max(this.textInner.scrollHeight, this.textInner.offsetHeight));
  }

  resolveSectionHeight() {
    if (!this.root) return false;

    if (this.isMobileViewport()) {
      this.root.style.height = '';
      this.root.classList.remove('tic--text-expanded');
      return false;
    }

    const base = this.getBaseSectionHeight();
    const padding = this.getVerticalPadding();
    const contentBudget = Math.max(0, base - padding);
    const textHeight = this.getTextContentHeight();
    const margin = this.getTextExpansionMargin();

    let resolved = base;

    if (textHeight + margin >= contentBudget) {
      const imageHeight = this.getImageHeight();
      const productsHeight =
        imageHeight > 0 ? imageHeight + Math.ceil(this.getProductsOverhead()) : 0;
      const contentHeight = Math.max(textHeight, imageHeight, productsHeight);
      resolved = Math.max(base, Math.ceil(contentHeight + padding));
    }

    this.root.style.height = `${resolved}px`;
    this.root.style.setProperty('--tic-section-height', `${resolved}px`);
    this.root.classList.toggle('tic--text-expanded', resolved > base);

    return resolved;
  }

  getImageHeight() {
    if (!this.imageWrap) return 0;

    const rect = this.imageWrap.getBoundingClientRect();
    const offsetHeight = this.imageWrap.offsetHeight;
    const height = Math.max(rect.height, offsetHeight);

    if (height > 0) return Math.round(height);

    const style = getComputedStyle(this.imageWrap);
    const width = rect.width || this.imageWrap.offsetWidth;
    const aspectRatio = style.aspectRatio;

    if (width > 0 && aspectRatio && aspectRatio !== 'auto') {
      const parts = aspectRatio.split('/');
      if (parts.length === 2) {
        const w = parseFloat(parts[0]);
        const h = parseFloat(parts[1]);
        if (w > 0 && h > 0) {
          return Math.round((width * h) / w);
        }
      }
    }

    const mainSize = parseFloat(getComputedStyle(this.root).getPropertyValue('--tic-main-image-size'));
    if (mainSize > 0 && !this.isMobileViewport()) {
      return Math.round(mainSize);
    }

    return 0;
  }

  measureVisibleStack(cards, count, gap, list) {
    if (!cards.length || !list) return 0;

    const lastCard = cards[Math.min(count, cards.length) - 1];
    const contentBottom = lastCard.offsetTop - list.offsetTop + lastCard.offsetHeight;

    let total = 0;
    for (let i = 0; i < count; i += 1) {
      total += cards[i].offsetHeight;
      if (i < count - 1) total += gap;
    }

    return Math.ceil(Math.max(total, contentBottom));
  }

  applyScrollAreaHeight(scrollAreaHeight, mobile) {
    const scrollVar = mobile ? '--tic-products-scroll-height-mobile' : '--tic-products-scroll-height-desktop';
    const heightPx = `${Math.max(0, Math.round(scrollAreaHeight))}px`;

    this.root.style.setProperty(scrollVar, heightPx);

    if (this.scrollWrap) {
      this.scrollWrap.style.flex = '0 0 auto';
      this.scrollWrap.style.height = heightPx;
      this.scrollWrap.style.maxHeight = heightPx;
    }

    if (this.scrollEl) {
      this.scrollEl.style.height = heightPx;
      this.scrollEl.style.maxHeight = heightPx;
    }
  }

  applyCardHeight(cardHeight, mobile) {
    const cardHeightVar = mobile ? '--tic-card-height-mobile' : '--tic-card-height-desktop';
    const thumbVar = mobile ? '--tic-card-thumb-mobile' : '--tic-card-thumb-desktop';
    const thumbSize = Math.max(56, cardHeight - 24);

    this.root.style.setProperty(cardHeightVar, `${cardHeight}px`);
    this.root.style.setProperty(thumbVar, `${thumbSize}px`);
    void this.scrollEl.offsetHeight;
  }

  syncDynamicLayout() {
    const list = this.scrollEl?.querySelector('.tic__products-list');
    const cards = this.getCards();

    if (!this.imageWrap || !this.productsPanel || !list || !cards.length || !this.root) {
      return false;
    }

    const imageHeight = this.getImageHeight();
    if (imageHeight <= 0) return false;

    const visible = parseInt(this.dataset.productsVisible || '2', 10);
    const count = Math.min(Math.max(visible, 1), cards.length);
    const gap = this.getListGap(list);
    const cardGapsTotal = count > 1 ? gap * (count - 1) : 0;
    const overhead = Math.ceil(this.getProductsOverhead());
    const scrollAreaHeight = Math.max(0, imageHeight - overhead - 8);
    const mobile = this.isMobileViewport();
    const minHeight = this.getCardMinHeight();

    if (scrollAreaHeight <= 0) return false;

    let cardHeight = Math.floor((scrollAreaHeight - cardGapsTotal) / count);
    if (cardHeight < minHeight) {
      cardHeight = minHeight;
    }

    this.applyCardHeight(cardHeight, mobile);

    let contentHeight = this.measureVisibleStack(cards, count, gap, list);
    let guard = 0;

    while (contentHeight > scrollAreaHeight && cardHeight > 80 && guard < 64) {
      cardHeight -= 1;
      this.applyCardHeight(cardHeight, mobile);
      contentHeight = this.measureVisibleStack(cards, count, gap, list);
      guard += 1;
    }

    const slack = scrollAreaHeight - contentHeight;
    if (slack > 0 && slack <= count) {
      cardHeight += 1;
      this.applyCardHeight(cardHeight, mobile);
      contentHeight = this.measureVisibleStack(cards, count, gap, list);
      if (contentHeight > scrollAreaHeight) {
        cardHeight -= 1;
        this.applyCardHeight(cardHeight, mobile);
        contentHeight = this.measureVisibleStack(cards, count, gap, list);
      }
    }

    const layoutKey = `${mobile}-${imageHeight}-${overhead}-${cardHeight}-${scrollAreaHeight}-${count}-${contentHeight}-${this.getBaseSectionHeight()}-${this.root.style.height || ''}`;

    if (layoutKey === this._layoutKey) {
      return true;
    }

    this._layoutKey = layoutKey;
    this.applyScrollAreaHeight(scrollAreaHeight, mobile);
    this.productsPanel.style.height = `${imageHeight}px`;
    this.productsPanel.style.maxHeight = `${imageHeight}px`;
    this.root.classList.add('tic--layout-synced');

    return true;
  }

  positionScrollNav() {
    if (!this.showScrollNav) return;

    const nav = this.querySelector('.tic__scroll-nav');
    const list = this.scrollEl?.querySelector('.tic__products-list');
    const cards = this.getCards();

    if (!nav || !list || cards.length < 2) return;

    const firstCard = cards[0];
    const gap = this.getListGap(list);
    const cardTop = firstCard.offsetTop - list.offsetTop;
    const breakY = cardTop + firstCard.offsetHeight + gap / 2;

    nav.style.top = `${breakY}px`;
  }

  onNavClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.scrollByStep(event.currentTarget === this.btnUp ? -1 : 1);
  }

  getCards() {
    return Array.from(this.scrollEl.querySelectorAll('.tic-product-card'));
  }

  getCardScrollTop(card) {
    const list = this.scrollEl.querySelector('.tic__products-list');
    if (!list) return card.offsetTop;

    return card.offsetTop - list.offsetTop;
  }

  getListPaddingBottom() {
    const list = this.scrollEl?.querySelector('.tic__products-list');
    if (!list) return 0;

    return parseFloat(getComputedStyle(list).paddingBottom) || 0;
  }

  getMaxScrollTop() {
    const cards = this.getCards();
    if (!cards.length) return 0;

    const lastCard = cards[cards.length - 1];
    const lastBottom = this.getCardScrollTop(lastCard) + lastCard.offsetHeight;
    const maxScroll = lastBottom + this.getListPaddingBottom() - this.scrollEl.clientHeight;

    return Math.max(0, Math.ceil(maxScroll));
  }

  scrollByStep(direction) {
    const cards = this.getCards();
    if (!cards.length) return;

    const scrollTop = this.scrollEl.scrollTop;
    let targetScroll = scrollTop;

    if (direction > 0) {
      const next = cards.find((card) => this.getCardScrollTop(card) > scrollTop + 4);
      targetScroll = next ? this.getCardScrollTop(next) : this.getMaxScrollTop();
    } else {
      const prev = [...cards].reverse().find((card) => this.getCardScrollTop(card) < scrollTop - 4);
      targetScroll = prev ? this.getCardScrollTop(prev) : 0;
    }

    this.scrollEl.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });
  }

  updateNavState() {
    if (!this.scrollEl) return;

    const { scrollTop } = this.scrollEl;
    const maxScrollTop = this.getMaxScrollTop();
    const atTop = scrollTop <= 2;
    const atBottom = scrollTop >= maxScrollTop - 2;

    if (this.btnUp) {
      this.btnUp.disabled = atTop;
      this.btnUp.setAttribute('aria-disabled', atTop ? 'true' : 'false');
    }

    if (this.btnDown) {
      this.btnDown.disabled = atBottom;
      this.btnDown.setAttribute('aria-disabled', atBottom ? 'true' : 'false');
    }
  }
}

if (!customElements.get('text-image-collection-tmb')) {
  customElements.define('text-image-collection-tmb', TextImageCollectionTmb);
}

function refreshAllTicSections(force = false) {
  document.querySelectorAll('text-image-collection-tmb').forEach((section) => {
    if (force && typeof section.forceRefreshLayout === 'function') {
      section.forceRefreshLayout();
      return;
    }

    if (typeof section.refreshLayout === 'function') {
      section.refreshLayout();
    }
  });
}

window.addEventListener('load', () => refreshAllTicSections(true));

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('text-image-collection-tmb').forEach((section) => {
    if (typeof section.forceRefreshLayout === 'function') {
      section.forceRefreshLayout();
    }
  });
});

document.addEventListener('shopify:section:reorder', () => refreshAllTicSections(true));
